import {
  useAudioTrack,
  useDaily,
  useLocalSessionId,
  useVideoTrack,
} from "@daily-co/daily-react";
import type { IconProps } from "@tamagui/helpers-icon";
import {
  MessageCircle,
  Mic,
  MicOff,
  Minimize2,
  Video,
  VideoOff,
} from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import { useMedia } from "tamagui";

import { XStack } from "@/components";
import { Phone } from "@/components/PhoneIcon";
import CallTrayButton from "./CallTrayButton";

type CallTrayProps = {
  leaveCall: () => void;
  minimizeCall: () => void;
};

const DownPhone = (props: IconProps) => <Phone {...props} rotate="135deg" />;

const CallTray = ({ leaveCall, minimizeCall }: CallTrayProps) => {
  const callObject = useDaily();
  const localSessionId = useLocalSessionId();
  const localVideo = useVideoTrack(localSessionId);
  const localAudio = useAudioTrack(localSessionId);
  const { show } = useToastController();
  const { lg } = useMedia();

  const handleToggleAudio = () => {
    const needsPermission =
      localAudio.blocked?.byPermissions ||
      (localAudio.state === "off" && !localAudio.off?.byUser);

    if (needsPermission) {
      show(
        "Please allow access to your microphone in settings and refresh the page.",
        { duration: Infinity },
      );
    } else {
      callObject?.setLocalAudio(localAudio.isOff);
    }
  };

  const handleToggleVideo = () => {
    const needsPermission =
      localVideo.blocked?.byPermissions ||
      (localVideo.state === "off" && !localVideo.off?.byUser);

    if (needsPermission) {
      show(
        "Please allow access to your camera in settings and refresh the page.",
        { duration: Infinity },
      );
    } else {
      callObject?.setLocalVideo(localVideo.isOff);
    }
  };

  return (
    <XStack
      flexShrink={0}
      justifyContent="space-around"
      alignItems="center"
      backgroundColor="$fillCallTray"
      padding="$250"
      $lg={{
        borderBottomLeftRadius: "$150",
        borderBottomRightRadius: "$150",
      }}
    >
      <CallTrayButton
        onPress={handleToggleAudio}
        Icon={localAudio.isOff ? MicOff : Mic}
        text={localAudio.isOff ? "Unmute" : "Mute"}
        backgroundColor={
          localAudio.isOff ? "$fillCallDanger" : "$fillCallControl"
        }
        ariaLabel={localAudio.isOff ? "Unmute microphone" : "Mute microphone"}
      />
      <CallTrayButton
        onPress={handleToggleVideo}
        Icon={localVideo.isOff ? VideoOff : Video}
        text={localVideo.isOff ? "Turn on" : "Turn off"}
        backgroundColor={
          localVideo.isOff ? "$fillCallDanger" : "$fillCallControl"
        }
        ariaLabel={localVideo.isOff ? "Turn camera on" : "Turn camera off"}
      />
      <CallTrayButton
        onPress={minimizeCall}
        Icon={lg ? Minimize2 : MessageCircle}
        text={lg ? "Minimize" : "Open chat"}
      />
      <CallTrayButton
        onPress={leaveCall}
        Icon={DownPhone}
        backgroundColor="$fillCallDanger"
        text="Leave call"
      />
    </XStack>
  );
};

export default CallTray;
